import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    accordionSummaryClasses,
    Alert,
    paletteColors,
    Typography,
} from '@evaneos/b2b-ui';
import ArrowRightIcon from '@evaneos/b2b-ui/icons/ArrowRight';
import DoneAll from '@evaneos/b2b-ui/icons/DoneAll';
import AssignmentOutlinedIcon from '@evaneos/b2b-ui/icons/AssignmentOutlined';
import BedOutlinedIcon from '@evaneos/b2b-ui/icons/BedOutlined';
import DescriptionOutlinedIcon from '@evaneos/b2b-ui/icons/DescriptionOutlined';
import LanguageIcon from '@evaneos/b2b-ui/icons/LanguageOutlined';
import CarIcon from '@evaneos/b2b-ui/icons/DirectionsCarFilledOutlined';
import HikingIcon from '@evaneos/b2b-ui/icons/HikingOutlined';
import PeopleIcon from '@evaneos/b2b-ui/icons/PeopleAltOutlined';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetDossierData, useGetTripProject } from '../../../resources/dossier/queries';
import { ChildrenAges, DossierId } from '../../../resources/dossier/types';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../shared/analytics';
import { DefinitionList } from '../../../design-system/DefinitionList/DefinitionList';

import { GlobalLoader } from '../../../shared/components/layout/GlobalLoader';
import { useLocale } from '../../../shared/providers/LocalizationProvider';

import { useDossierLayoutInfo } from '../DossierLayoutInfoContext';

import { ItineraryInfo } from './ItineraryInfo';

export const InitialRequest = () => {
    const { formatMessage, formatDate } = useIntl();
    const { dossierId } = useParams<{ dossierId: DossierId }>();
    const { trackEvent } = useTrackEventAmplitude();
    const { locale } = useLocale();

    const { isFirstColumnNarrow } = useDossierLayoutInfo();

    const { dossier, participants } = useGetDossierData(dossierId) || {};
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const { data, status } = useGetTripProject(dossierId, locale);

    const requestDate = dossier?.requestDate;

    const formatAges = (ages: ChildrenAges | null): string => {
        if (!ages || ages.length === 0) {
            return '';
        }

        const formattedAges = ages
            .map((age) =>
                formatMessage({ id: 'dossier.participants.children_ages' }, { childrenAge: age }),
            )
            .join(' - ');

        return `(${formattedAges})`;
    };

    const handleAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
        if (isExpanded) {
            trackEvent((ampli, defaultProperties) => {
                ampli.detailsInitialRequestClicked({
                    ...defaultProperties,
                    ...trackedTripProperties,
                });
            });
        }
    };

    return (
        <Accordion
            elevation={0}
            disableGutters
            defaultExpanded
            onChange={handleAccordionChange}
            sx={{
                '&::before': {
                    display: 'none',
                },
                [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
                    {
                        transform: 'rotate(90deg)',
                    },
                backgroundColor: paletteColors.neutral[100],
                borderRadius: 2,
                py: 1,
                px: 2,
            }}
        >
            <AccordionSummary
                expandIcon={<ArrowRightIcon htmlColor={paletteColors.neutral[700]} />}
                sx={{
                    flexDirection: 'row-reverse',
                    padding: 0,
                    gap: 1,
                    [`&.${accordionSummaryClasses.focusVisible}`]: {
                        backgroundColor: paletteColors.green[200],
                    },
                }}
            >
                <Typography variant="h4" component="strong">
                    {formatMessage({ id: 'dossier.information.initial_request.title' })}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
                {status === 'pending' && (
                    <Typography variant="body2" my={0.5}>
                        <GlobalLoader />
                    </Typography>
                )}

                {status === 'error' && (
                    <Alert severity="error">
                        {formatMessage({ id: 'dossier.information.initial_request.error' })}
                    </Alert>
                )}

                {status === 'success' && (
                    <DefinitionList>
                        <DefinitionList.Item>
                            <DefinitionList.Label
                                id="request-date"
                                icon={<DoneAll fontSize="small" aria-hidden="true" />}
                                iconOnly={isFirstColumnNarrow}
                            >
                                {formatMessage({
                                    id: 'dossier.information.initial_request.received_date',
                                })}
                            </DefinitionList.Label>
                            <DefinitionList.StaticValue>
                                <Typography variant="body2">
                                    {formatDate(requestDate, {
                                        day: '2-digit',
                                        month: 'long',
                                        year: 'numeric',
                                        hour: 'numeric',
                                        minute: 'numeric',
                                    })}
                                </Typography>
                            </DefinitionList.StaticValue>
                        </DefinitionList.Item>
                        <ItineraryInfo tripProject={data} />
                        {participants && (
                            <DefinitionList.Item>
                                <DefinitionList.Label
                                    id="number-of-travellers"
                                    icon={<PeopleIcon fontSize="small" aria-hidden="true" />}
                                    iconOnly={isFirstColumnNarrow}
                                >
                                    {formatMessage({
                                        id: 'dossier.information.number_of_travellers',
                                    })}
                                </DefinitionList.Label>
                                <DefinitionList.StaticValue>
                                    <Typography variant="body2">
                                        {formatMessage(
                                            { id: 'dossier.participants.counter' },
                                            {
                                                ...participants.number,
                                                teenagersAges:
                                                    participants.childrenAges &&
                                                    formatAges(participants.childrenAges.teenagers),
                                                childrenAges:
                                                    participants.childrenAges &&
                                                    formatAges(participants.childrenAges.children),
                                                babiesAges:
                                                    participants.childrenAges &&
                                                    formatAges(participants.childrenAges.babies),
                                            },
                                        )}
                                    </Typography>
                                </DefinitionList.StaticValue>
                            </DefinitionList.Item>
                        )}
                        {data.projectStep && (
                            <DefinitionList.Item>
                                <DefinitionList.Label
                                    id="project-step"
                                    icon={
                                        <AssignmentOutlinedIcon
                                            aria-hidden="true"
                                            fontSize="small"
                                        />
                                    }
                                    iconOnly={isFirstColumnNarrow}
                                >
                                    {formatMessage({
                                        id: 'dossier.information.initial_request.project_step',
                                    })}
                                </DefinitionList.Label>
                                <DefinitionList.StaticValue>
                                    <Typography variant="body2">
                                        {data.projectStep.valueTranslated}
                                    </Typography>
                                </DefinitionList.StaticValue>
                            </DefinitionList.Item>
                        )}
                        {data.productRange && (
                            <DefinitionList.Item>
                                <DefinitionList.Label
                                    id="product-range"
                                    icon={<HikingIcon fontSize="small" aria-hidden="true" />}
                                    iconOnly={isFirstColumnNarrow}
                                >
                                    {formatMessage({
                                        id: 'dossier.information.initial_request.product_range',
                                    })}
                                </DefinitionList.Label>
                                <DefinitionList.StaticValue>
                                    <Typography variant="body2">
                                        {data.productRange.valueTranslated.join(', ')}
                                    </Typography>
                                </DefinitionList.StaticValue>
                            </DefinitionList.Item>
                        )}
                        {data.hosting && (
                            <DefinitionList.Item>
                                <DefinitionList.Label
                                    id="hosting"
                                    icon={<BedOutlinedIcon fontSize="small" aria-hidden="true" />}
                                    iconOnly={isFirstColumnNarrow}
                                >
                                    {formatMessage({
                                        id: 'dossier.information.initial_request.hosting',
                                    })}
                                </DefinitionList.Label>
                                <DefinitionList.StaticValue>
                                    {data.hosting.valueTranslated.map((value, index) => (
                                        <Typography key={index} variant="body2">
                                            {value}
                                        </Typography>
                                    ))}
                                </DefinitionList.StaticValue>
                            </DefinitionList.Item>
                        )}
                        {data.accompaniment && (
                            <DefinitionList.Item>
                                <DefinitionList.Label
                                    id="accompaniment"
                                    icon={<CarIcon fontSize="small" aria-hidden="true" />}
                                    iconOnly={isFirstColumnNarrow}
                                >
                                    {formatMessage({
                                        id: 'dossier.information.initial_request.accompaniment',
                                    })}
                                </DefinitionList.Label>
                                <DefinitionList.StaticValue>
                                    <Typography variant="body2">
                                        {data.accompaniment.valueTranslated}
                                    </Typography>
                                </DefinitionList.StaticValue>
                            </DefinitionList.Item>
                        )}
                        {data.guideLanguage && (
                            <DefinitionList.Item>
                                <DefinitionList.Label
                                    id="guide-language"
                                    icon={<LanguageIcon fontSize="small" aria-hidden="true" />}
                                    iconOnly={isFirstColumnNarrow}
                                >
                                    {formatMessage({
                                        id: 'dossier.information.initial_request.guide_language',
                                    })}
                                </DefinitionList.Label>
                                <DefinitionList.StaticValue>
                                    <Typography variant="body2">
                                        {data.guideLanguage.valueTranslated.join(', ')}
                                    </Typography>
                                </DefinitionList.StaticValue>
                            </DefinitionList.Item>
                        )}
                        {data.project && (
                            <DefinitionList.Item>
                                <DefinitionList.Label
                                    id="project"
                                    icon={
                                        <DescriptionOutlinedIcon
                                            fontSize="small"
                                            aria-hidden="true"
                                        />
                                    }
                                    iconOnly={isFirstColumnNarrow}
                                >
                                    {formatMessage({
                                        id: 'dossier.information.initial_request.description',
                                    })}
                                </DefinitionList.Label>
                                <DefinitionList.StaticValue>
                                    <Typography variant="body2">
                                        {data.project.valueTranslated}
                                    </Typography>
                                </DefinitionList.StaticValue>
                            </DefinitionList.Item>
                        )}
                    </DefinitionList>
                )}
            </AccordionDetails>
        </Accordion>
    );
};
